// ============================================================
// StepAddress Component
// ============================================================

import { TextField, Grid, Typography, FormControlLabel, Checkbox, Divider, Box, MenuItem, Autocomplete } from '@mui/material';
import { INDIAN_STATES } from '@/constants/states';
import { 
  nameRegex, 
  digitsOnlyRegex, 
  addressRegex, 
  createRestrictedHandler
} from '@/utils/inputRestrictions';

export function StepAddress({ formik }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;

  const handleSameAsCurrentChange = (e) => {
    const checked = e.target.checked;
    setFieldValue('sameAsCurrent', checked);
    if (checked) {
      setFieldValue('permanent_address', values.current_address);
      setFieldValue('permanent_city', values.current_city);
      setFieldValue('permanent_state', values.current_state);
      setFieldValue('permanent_country', values.current_country || 'India');
      setFieldValue('permanent_pincode', values.current_pincode);
    } else {
      setFieldValue('permanent_address', '');
      setFieldValue('permanent_city', '');
      setFieldValue('permanent_state', '');
      setFieldValue('permanent_country', 'India');
      setFieldValue('permanent_pincode', '');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
        <Typography variant="subtitle1" color="primary">Current Address</Typography>
      </Grid>
      
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Address Line *"
          name="current_address"
          value={values.current_address}
          onChange={createRestrictedHandler(handleChange, addressRegex)}
          onBlur={handleBlur}
          error={touched.current_address && Boolean(errors.current_address)}
          helperText={touched.current_address && errors.current_address}
        />
      </Grid>
      
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="City *"
          name="current_city"
          value={values.current_city}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          error={touched.current_city && Boolean(errors.current_city)}
          helperText={touched.current_city && errors.current_city}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Autocomplete
          options={INDIAN_STATES}
          value={values.current_state || null}
          onChange={(event, newValue) => {
            setFieldValue('current_state', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="State *"
              name="current_state"
              onBlur={handleBlur}
              error={touched.current_state && Boolean(errors.current_state)}
              helperText={touched.current_state && errors.current_state}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Country *"
          name="current_country"
          value={values.current_country || 'India'}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          error={touched.current_country && Boolean(errors.current_country)}
          helperText={touched.current_country && errors.current_country}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Pincode *"
          name="current_pincode"
          value={values.current_pincode}
          onChange={createRestrictedHandler(handleChange, digitsOnlyRegex, 6)}
          onBlur={handleBlur}
          error={touched.current_pincode && Boolean(errors.current_pincode)}
          helperText={touched.current_pincode && errors.current_pincode}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" color="primary">Permanent Address</Typography>
          <FormControlLabel
            control={
              <Checkbox 
                checked={values.sameAsCurrent} 
                onChange={handleSameAsCurrentChange}
                color="primary" 
              />
            }
            label="Same as Current Address"
          />
        </Box>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Address Line *"
          name="permanent_address"
          value={values.permanent_address}
          onChange={createRestrictedHandler(handleChange, addressRegex)}
          onBlur={handleBlur}
          disabled={values.sameAsCurrent}
          error={touched.permanent_address && Boolean(errors.permanent_address)}
          helperText={touched.permanent_address && errors.permanent_address}
        />
      </Grid>
      
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="City *"
          name="permanent_city"
          value={values.permanent_city}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          disabled={values.sameAsCurrent}
          error={touched.permanent_city && Boolean(errors.permanent_city)}
          helperText={touched.permanent_city && errors.permanent_city}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Autocomplete
          options={INDIAN_STATES}
          value={values.permanent_state || null}
          onChange={(event, newValue) => {
            setFieldValue('permanent_state', newValue || '');
          }}
          disabled={values.sameAsCurrent}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="State *"
              name="permanent_state"
              onBlur={handleBlur}
              error={touched.permanent_state && Boolean(errors.permanent_state)}
              helperText={touched.permanent_state && errors.permanent_state}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Country *"
          name="permanent_country"
          value={values.permanent_country || 'India'}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          disabled={values.sameAsCurrent}
          error={touched.permanent_country && Boolean(errors.permanent_country)}
          helperText={touched.permanent_country && errors.permanent_country}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Pincode *"
          name="permanent_pincode"
          value={values.permanent_pincode}
          onChange={createRestrictedHandler(handleChange, digitsOnlyRegex, 6)}
          onBlur={handleBlur}
          disabled={values.sameAsCurrent}
          error={touched.permanent_pincode && Boolean(errors.permanent_pincode)}
          helperText={touched.permanent_pincode && errors.permanent_pincode}
        />
      </Grid>
    </Grid>
  );
}
